import { useState } from "react"
import moment from "moment"
import helpers from "helpers/helpers"
import FullTable from "components/FullTable"
import Switch from "react-switch"

const LotInventoryReplacement = (props) => {
	const [substantiallyOnly, setSubstantiallyOnly] = useState(false)
	const getTrProps = (state, rowInfo, instance) => {
		if (rowInfo) {
			let v = rowInfo.original
			let months = parseInt(v.four_mo_remaining)
			if (months <= 5) {
				return { className: "!bg-red-300" }
			} else if (months >= 5 && months <= 9) {
				return { className: "!bg-red-200" }
			} else if (months >= 10 && months <= 12) {
				return { className: "!bg-orange-200" }
			} else if (months >= 13) {
				return { className: "statusColor--approved" }
			}
		}
		return {}
	}
	const buildData = () => {
		// This method just returns an object with lot types as keys and returns the sales pace for both 4 months and 12 months.
		let store = {}
		let fourMos = moment().subtract(4, "months").unix()
		let twelveMos = moment().subtract(1, "year").unix()
		let dataModel = {
			four_mo: 0,
			four_mo_total: 0,
			four_mo_remaining: 0,
			twelve_mo: 0,
			twelve_mo_total: 0,
			twelve_mo_remaining: 0,
			remaining: 0,
			specs: 0,
			vdl: 0,
		}

		// If all lots in a lot type have sale_id's, then ignore that lot type - it's sold out and should not be counted.
		let goodLotTypes = []
		_.forEach(props.payload.exec.lots, (v) => {
			if (substantiallyOnly && !v.substantially_complete) return
			if (!v.sale_id && !goodLotTypes.includes(v.lot_type)) {
				goodLotTypes.push(v.lot_type)
			}
		})
		//console.log(goodLotTypes)

		_.forEach(props.payload.exec.lots, (v) => {
			if (substantiallyOnly && !v.substantially_complete) return
			let type = _.find(props.payload.public.lot_types, { id: v.lot_type })
			if (!type || !goodLotTypes.includes(v.lot_type)) {
				return
			}
			let path = `["${type.name}"]`
			if (!_.has(store, path)) {
				let model = _.clone(dataModel)
				_.set(store, path, model)
			}
			if (v.start_date && !v.sale_id && !v.model) {
				// spec inventory
				store[type.name].specs++
				store[type.name].remaining++
			}
			if (!v.start_date && !v.complete_date && !v.sale_id && !v.model && !v.buyer_id) {
				// VDL
				store[type.name].vdl++
				store[type.name].remaining++
			}
		})

		_.forEach(props.payload.exec.sales, (v) => {
			let lot = _.find(props.payload.exec.lots, { id: v.lot_id })
			if(!lot) {
				return
			}
			if(substantiallyOnly && !lot.substantially_complete) return
			if (v.bustout_date || v.transfer_date) {
				return
			}
			let type = _.find(props.payload.public.lot_types, { id: v.lot_type })
			if (!type) {
				return
			}
			let path = `["${type.name}"]`
			if (!_.has(store, path)) {
				let model = _.clone(dataModel)
				_.set(store, path, model)
			}
			if (!type || !goodLotTypes.includes(v.lot_type)) {
				return
			}
			// 4-mo average - if  sale was within the last 4 months
			if (v.sale_date > fourMos) {
				store[type.name].four_mo++
				store[type.name].four_mo_total++
			}
			// sale was within the past 12 months
			if (v.sale_date > twelveMos) {
				store[type.name].twelve_mo++
				store[type.name].twelve_mo_total++
			}
		})
		// Cleaned takes in the store and removes any of the rows that do not have combined_remaining, since they shouldnt' show if there's no inventory.
		return store
	}

	const buildReplacementTable = () => {
		let data = buildData()
		let ret = []
		_.forEach(data, (v, k) => {
			if (v.remaining && v.four_mo) {
				v.four_mo = parseFloat((v.four_mo / 4).toFixed(2))
				v.four_mo_remaining = parseFloat((v.remaining / v.four_mo).toFixed(2))
			}
			if (v.remaining && v.twelve_mo) {
				v.twelve_mo = parseFloat((v.twelve_mo / 12).toFixed(2))
				v.twelve_mo_remaining = parseFloat((v.remaining / v.twelve_mo).toFixed(2))
			}
			let f = {
				lot_type: k,
				specs: data[k]?.specs || 0,
				vdl: data[k]?.vdl || 0,
				four_mo: data[k]?.four_mo || 0,
				four_mo_remaining: data[k]?.four_mo_remaining || 0,
				twelve_mo: data[k]?.twelve_mo || 0,
				twelve_mo_remaining: data[k]?.twelve_mo_remaining || 0,
			}
			ret.push(f)
		})
		return ret
	}

	const Legend = () => {
		return (
			<ul className="legend__list flex-5">
				<li>
					<span className="bg-red-300" />
					Sellout &lt; 5 Months
				</li>
				<li>
					<span className="bg-red-200" />
					Sellout 5-9 Months
				</li>
				<li>
					<span className="bg-orange-200" />
					Sellout 9-12 Months
				</li>
				<li>
					<span className="bg-green-200" />
					Sellout 12+ Months
				</li>
			</ul>
		)
	}

	const replacement = [
		{
			Header: "Lot Type",
			accessor: "lot_type",
			filterMethod: (f, r) => helpers.tableText(f, r, "lot_type"),
		},
		{
			Header: "Specs",
			accessor: "specs",
			filterMethod: (f, r) => helpers.tableText(f, r, "specs"),
			Footer: (props) => helpers.tableTotals(props, "specs", "0,0"),
		},
		{
			Header: "VDL",
			accessor: "vdl",
			filterMethod: (f, r) => helpers.tableText(f, r, "vdl"),
			Footer: (props) => helpers.tableTotals(props, "vdl", "0,0"),
		},
		{
			Header: "Sales Rate (4 mos)",
			accessor: "four_mo",
			filterMethod: (f, r) => helpers.tableText(f, r, "four_mo"),
			Footer: (props) => helpers.tableAverages(props, "four_mo", "0.00"),
		},
		{
			Header: "Remaining Months (4 mos)",
			accessor: "four_mo_remaining",
			filterMethod: (f, r) => helpers.tableText(f, r, "four_mo_remaining"),
			Footer: (props) => helpers.tableAverages(props, "four_mo_remaining", "0.00"),
		},
		{
			Header: "Sales Rate (12 mos)",
			accessor: "twelve_mo",
			filterMethod: (f, r) => helpers.tableText(f, r, "twelve_mo"),
			Footer: (props) => helpers.tableAverages(props, "twelve_mo", "0.00"),
		},
		{
			Header: "Remaining Months (12 mos)",
			accessor: "twelve_mo_remaining",
			filterMethod: (f, r) => helpers.tableText(f, r, "twelve_mo_remaining"),
			Footer: (props) => helpers.tableAverages(props, "twelve_mo_remaining", "0.00"),
		},
	]

	return (
		<>
			<h2>Lot Inventory Replacement</h2>
			<p className="text-xs mt-2">Based on VDL plus spec homes.</p>
			<Legend />
			<div className="switch" style={{ marginTop: 15 }} onClick={(e) => e.stopPropagation()}>
				<Switch
					onChange={() => setSubstantiallyOnly(!substantiallyOnly)}
					checked={substantiallyOnly}
					checkedIcon={false}
					uncheckedIcon={false}
					key={"switch"}
					id={"switch"}
				/>
				<p>Showing {substantiallyOnly ? "Delivered Only Sections" : "All Sections"}</p>
			</div>
			<FullTable
				data={buildReplacementTable()}
				columns={replacement}
				name="vacant__replacement"
				title="Lot Inventory Replacement"
				classNames="small"
				pageSize={20}
				getTrProps={getTrProps}
			/>
		</>
	)
}

export default LotInventoryReplacement
